import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaLock, FaArrowRight, FaShieldAlt, FaChartLine, FaArrowUp, FaArrowDown, FaRocket, FaCoins, FaBullseye, FaPercent } from 'react-icons/fa';
import { motion } from 'framer-motion';
import bull from '../../assets/images/bull.png';

const HeroSection = ({ contentData }) => {
  const navigate = useNavigate();
  const [marketData, setMarketData] = useState({
    nifty: { price: 0, formattedPrice: '₹0', change: 0 },
    sensex: { price: 0, formattedPrice: '₹0', change: 0 }
  });
  const [loading, setLoading] = useState(true);

  // API Configuration
  const API_URL = 'https://sheets.googleapis.com/v4/spreadsheets/1fz2UjsSid3aJab3LbZI2zbNLeqUkDuyPWmCsnTk0mLY/values/%27NIFTY%2050%27?key=AIzaSyBBDGaGd_H-xl8tjzlIZC-Yx2QLMM5FVUA';

  useEffect(() => {
    fetchMarketData();
    // Auto-refresh every 30 seconds
    const intervalId = setInterval(fetchMarketData, 30000);
    return () => clearInterval(intervalId);
  }, []);

  const fetchMarketData = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URL);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (!result.values) {
        throw new Error('No data found in the response');
      }
      
      const processedData = processData(result.values);
      setMarketData(processedData);
      setLoading(false);
      
    } catch (err) {
      console.error('Error fetching market data:', err);
      // Set default values if API fails
      setMarketData({
        nifty: { price: 22500.15, formattedPrice: '₹22,500', change: 1.25 },
        sensex: { price: 74150.30, formattedPrice: '₹74,150', change: 1.10 }
      });
      setLoading(false);
    }
  };

  // Process data from Google Sheets
  const processData = (values) => {
    let niftyPrice = 22500.15; // Default values
    let sensexPrice = 74150.30;

    if (values) {
      for (let i = 0; i < values.length; i++) {
        const row = values[i];
        if (!row || row.length === 0) continue;

        // Extract NIFTY 50 data
        if (row[0] === 'NIFTY_50' && row[1] === 'NIFTY 50' && row[2]) {
          niftyPrice = parseFloat(row[2]) || 22500.15;
        }

        // Extract SENSEX data
        if (row[13] === 'Indexbom:sensex ' && row[14] === 'BSE SENSEX' && row[15]) {
          sensexPrice = parseFloat(row[15]) || 74150.30;
        }
      }
    }

    // Format Indian currency
    const formatIndianCurrency = (amount) => {
      const num = parseFloat(amount);
      if (isNaN(num)) return '₹0';
      return new Intl.NumberFormat('en-IN', {
        maximumFractionDigits: 0
      }).format(num);
    };

    // Calculate random change for display (in a real app, this would come from API)
    const niftyChange = 0.5 + Math.random() * 1.5; // Random between 0.5% and 2%
    const sensexChange = 0.4 + Math.random() * 1.6; // Random between 0.4% and 2%

    return {
      nifty: {
        price: niftyPrice,
        formattedPrice: formatIndianCurrency(niftyPrice.toString()),
        change: niftyChange.toFixed(2)
      },
      sensex: {
        price: sensexPrice,
        formattedPrice: formatIndianCurrency(sensexPrice.toString()),
        change: sensexChange.toFixed(2)
      }
    };
  };

  // NEW: Market Indicator Component
  const MarketIndicator = ({ index, price, change, loading }) => {
    const isPositive = parseFloat(change) > 0;
    const changeValue = parseFloat(change);
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        whileHover={{ scale: 1.05 }}
        className="relative bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl p-4 shadow-lg"
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-800 text-lg">{index}</h3>
            {loading ? (
              <div className="flex items-center space-x-2 mt-1">
                <div className="w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="w-12 h-4 bg-gray-200 rounded animate-pulse"></div>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold text-gray-900 mt-1">{price}</div>
                <div className={`flex items-center mt-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                  {isPositive ? <FaArrowUp className="mr-1" /> : <FaArrowDown className="mr-1" />}
                  <span className="font-semibold">{Math.abs(changeValue)}%</span>
                  <span className="text-xs ml-2">Today</span>
                </div>
              </>
            )}
          </div>
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            isPositive ? 'bg-green-100' : 'bg-red-100'
          }`}>
            {loading ? (
              <div className="w-6 h-6 bg-gray-300 rounded animate-pulse"></div>
            ) : isPositive ? (
              <FaArrowUp className={`text-xl ${isPositive ? 'text-green-600' : 'text-red-600'}`} />
            ) : (
              <FaArrowDown className={`text-xl ${isPositive ? 'text-green-600' : 'text-red-600'}`} />
            )}
          </div>
        </div>
        
        {/* Mini chart */}
        <div className="mt-3">
          {loading ? (
            <div className="h-12 bg-gray-100 rounded animate-pulse"></div>
          ) : (
            <div className="h-12 flex items-end space-x-1">
              {[30, 45, 35, 60, 40, 70, 55].map((height, idx) => (
                <motion.div
                  key={idx}
                  initial={{ height: 0 }}
                  animate={{ height: `${height}%` }}
                  transition={{ duration: 1, delay: idx * 0.1 }}
                  className={`w-3 rounded-t ${isPositive ? 'bg-gradient-to-t from-green-400 to-green-600' : 'bg-gradient-to-t from-red-400 to-red-600'}`}
                />
              ))}
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50 py-20 md:py-16" id='hero'>
      {/* Background Pattern - Original kept */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-64 h-64 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-64 h-64 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* UPDATED: Left Side Floating Elements with Live Market Data */}
      <div className="absolute left-4 md:left-8 top-1/4 hidden lg:block">
        {/* NIFTY 50 Indicator */}
        <MarketIndicator
          index="NIFTY 50"
          price={marketData.nifty.formattedPrice}
          change={marketData.nifty.change}
          loading={loading}
        />

        <motion.div
          animate={{ y: [0, 15, 0] }}
          transition={{ duration: 5, repeat: Infinity, delay: 1 }}
          className="relative mt-6 bg-gradient-to-br from-blue-100/50 to-white/50 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-purple-200/30"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-700 font-semibold">Market Pulse</span>
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <FaChartLine className="text-green-600 text-sm" />
            </div>
          </div>
          {loading ? (
            <div className="space-y-2">
              <div className="h-2 bg-gray-200 rounded-full animate-pulse"></div>
              <div className="h-2 bg-gray-200 rounded-full animate-pulse"></div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Volume</span>
                <span className="font-semibold text-green-600">↑ 85%</span>
              </div>
              <div className="h-1 w-full bg-gray-200 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "85%" }}
                  transition={{ duration: 2 }}
                  className="h-full bg-gradient-to-r from-green-400 to-emerald-500"
                />
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* UPDATED: Right Side Floating Elements with Live Market Data */}
      <div className="absolute right-4 md:right-8 top-1/3 hidden lg:block">
        {/* SENSEX Indicator */}
        <MarketIndicator
          index="BSE SENSEX"
          price={marketData.sensex.formattedPrice}
          change={marketData.sensex.change}
          loading={loading}
        />

        <motion.div
          animate={{ y: [0, 20, 0] }}
          transition={{ duration: 6, repeat: Infinity, delay: 1.5 }}
          className="relative mt-6 bg-gradient-to-br from-purple-100/50 to-white/50 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-blue-200/30"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-700 font-semibold">Live Indices</span>
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <FaPercent className="text-blue-600 text-sm" />
            </div>
          </div>
          {loading ? (
            <div className="h-12 bg-gray-100 rounded animate-pulse"></div>
          ) : (
            <div className="flex items-end space-x-1">
              {[40, 60, 80, 95, 75, 85].map((height, idx) => (
                <motion.div
                  key={idx}
                  initial={{ height: 0 }}
                  animate={{ height: `${height}%` }}
                  transition={{ duration: 1, delay: idx * 0.1 }}
                  className="w-3 bg-gradient-to-t from-blue-400 to-blue-600 rounded-t"
                />
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Animated Market Elements Overlay */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Floating Stock Symbols */}
        <motion.div
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="absolute top-10 right-10 text-4xl opacity-50"
        >
          <img src={bull} alt="Chart Increasing" className="w-50" 
          height={50}
          width={50}
           />
        </motion.div>
        <motion.div
          animate={{ y: [0, 15, 0] }}
          transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
          className="absolute top-20 left-20 text-3xl opacity-10"
        >
          ₹
        </motion.div>
        <motion.div
          animate={{ y: [0, -15, 0] }}
          transition={{ duration: 3.5, repeat: Infinity, delay: 1 }}
          className="absolute bottom-20 left-1/4 text-4xl opacity-10"
        >
          %
        </motion.div>
        <motion.div
          animate={{ y: [0, 20, 0] }}
          transition={{ duration: 4, repeat: Infinity, delay: 1.5 }}
          className="absolute bottom-10 right-1/3 text-5xl opacity-10"
        >
          📊
        </motion.div>

        {/* Animated Chart Lines */}
        <svg className="absolute inset-0 w-full h-full opacity-5">
          <motion.path
            d="M0,300 Q100,200 200,250 Q300,300 400,200 Q500,100 600,250 Q700,400 800,300"
            fill="none"
            stroke="#3b82f6"
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 3, repeat: Infinity }}
          />
          <motion.path
            d="M0,350 Q150,300 300,350 Q450,400 600,300 Q750,200 900,350"
            fill="none"
            stroke="#8b5cf6"
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
          />
        </svg>

        {/* Animated Market Candles */}
        <div className="absolute top-1/3 left-1/4 flex space-x-4">
          <motion.div
            animate={{ height: ["20px", "40px", "20px"] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-3 bg-green-400 opacity-20 rounded-t"
          />
          <motion.div
            animate={{ height: ["40px", "20px", "40px"] }}
            transition={{ duration: 2.5, repeat: Infinity, delay: 0.3 }}
            className="w-3 bg-red-400 opacity-20 rounded-t"
          />
          <motion.div
            animate={{ height: ["30px", "50px", "30px"] }}
            transition={{ duration: 3, repeat: Infinity, delay: 0.6 }}
            className="w-3 bg-green-400 opacity-20 rounded-t"
          />
        </div>

        {/* Floating Price Tags with Live Data */}
        <motion.div
          animate={{ x: [0, 100, 0] }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-1/4 right-10 opacity-10"
        >
          <div className="flex items-center bg-white/20 px-3 py-1 rounded-full shadow">
            <span className="text-green-500 text-sm font-bold">
              NIFTY: {marketData.nifty.formattedPrice}
            </span>
          </div>
        </motion.div>

        <motion.div
          animate={{ x: [100, 0, 100] }}
          transition={{ duration: 10, repeat: Infinity, delay: 2 }}
          className="absolute bottom-1/4 left-10 opacity-10"
        >
          <div className="flex items-center bg-white/20 px-3 py-1 rounded-full shadow">
            <span className="text-blue-500 text-sm font-bold">
              SENSEX: {marketData.sensex.formattedPrice}
            </span>
          </div>
        </motion.div>

        {/* Additional Bottom Elements */}
        <div className="absolute bottom-32 left-1/4 hidden lg:flex space-x-6 opacity-20">
          {[1, 2, 3, 4, 5].map((i) => (
            <motion.div
              key={i}
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
              className="w-12 h-1 bg-gradient-to-r from-blue-400 to-purple-400 rounded"
            />
          ))}
        </div>

        <div className="absolute bottom-40 right-1/4 hidden lg:flex space-x-4 opacity-20">
          {[1, 2, 3].map((i) => (
            <motion.div
              key={i}
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
              className="w-6 h-6 border-2 border-blue-400 rounded-full"
            />
          ))}
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          {/* Main Headline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-8"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600">
                 {contentData?.hero_title}
              </span>
              <br />
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              {contentData?.hero_subtitle}
            </p>
          </motion.div>

          {/* Live Market Ticker */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="mb-8 bg-white/90 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-gray-200 max-w-2xl mx-auto lg:hidden"
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold text-gray-700">Live Market Data</span>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            </div>
            
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
              {loading ? (
                <>
                  <div className="flex items-center space-x-3">
                    <div className="w-20 h-6 bg-gray-200 rounded animate-pulse"></div>
                    <div className="w-12 h-6 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-20 h-6 bg-gray-200 rounded animate-pulse"></div>
                    <div className="w-12 h-6 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center space-x-3">
                    <span className="font-bold text-gray-700">NIFTY 50</span>
                    <span className="text-gray-600">{marketData.nifty.formattedPrice}</span>
                    <span className={`flex items-center ${marketData.nifty.change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {marketData.nifty.change > 0 ? <FaArrowUp className="mr-1" /> : <FaArrowDown className="mr-1" />}
                      {Math.abs(marketData.nifty.change)}%
                    </span>
                  </div>
                  
                  <div className="w-px h-6 bg-gray-300 hidden md:block"></div>
                  
                  <div className="flex items-center space-x-3">
                    <span className="font-bold text-gray-700">BSE SENSEX</span>
                    <span className="text-gray-600">{marketData.sensex.formattedPrice}</span>
                    <span className={`flex items-center ${marketData.sensex.change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {marketData.sensex.change > 0 ? <FaArrowUp className="mr-1" /> : <FaArrowDown className="mr-1" />}
                      {Math.abs(marketData.sensex.change)}%
                    </span>
                  </div>
                </>
              )}
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(contentData?.hero_button_link1)}
              className="relative overflow-hidden px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-600 text-white font-bold rounded-xl shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-3 text-lg group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              <FaLock className="relative z-10 group-hover:scale-110 transition-transform" />
              <span className="relative z-10">{contentData?.hero_button_text1}</span>
              <FaArrowRight className="relative z-10 group-hover:translate-x-1 transition-transform" />
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.open(contentData?.hero_button_link2, '_blank')}
              className="px-8 py-4 border-2 border-blue-600 text-blue-600 font-bold rounded-xl hover:bg-blue-50 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 group"
            >
              <FaRocket className="group-hover:rotate-12 transition-transform" />
              <span>{contentData?.hero_button_text2}</span>
            </motion.button>
          </motion.div>

          {/* Features Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-16"
          >
            {[
              { 
                title: contentData?.hero_card1_title, 
                desc: contentData?.hero_card1_text, 
                color: 'from-blue-500 to-blue-600',
                icon: <FaShieldAlt className="text-white mb-2 text-2xl" />,
                animation: "animate-pulse"
              },
              { 
                title: contentData?.hero_card2_title, 
                desc: contentData?.hero_card2_text, 
                color: 'from-purple-500 to-purple-600',
                icon: <div className="text-white mb-2 text-2xl">👥</div>,
                animation: "animate-bounce-slow"
              },
              { 
                title: contentData?.hero_card3_title, 
                desc: contentData?.hero_card3_text, 
                color: 'from-green-500 to-green-600',
                icon: <FaChartLine className="text-white mb-2 text-2xl" />,
                animation: "animate-pulse animation-delay-1000"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.1, delay: 0.01 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className={`relative overflow-hidden bg-gradient-to-r ${feature.color} text-white p-6 rounded-2xl shadow-lg transform transition-all duration-300`}
              >
                <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000`}></div>
                
                <div className="relative z-10">
                  <div className={`${feature.animation} flex justify-center mb-3`}>
                    {feature.icon}
                  </div>
                  <div className="text-3xl font-bold mb-2">{feature.title}</div>
                  <div className="text-blue-100">{feature.desc}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Animated Market Ticker */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="mt-12 bg-white/80 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-gray-200 max-w-2xl mx-auto"
          >
            <div className="flex items-center justify-center gap-6 overflow-hidden">
              <div className="flex items-center space-x-2 animate-marquee whitespace-nowrap">
                {[
                  { symbol: "NIFTY", price: marketData.nifty.formattedPrice, change: marketData.nifty.change > 0 ? `+${marketData.nifty.change}%` : `${marketData.nifty.change}%` },
                  { symbol: "BANKNIFTY", price: "₹48,200", change: "+0.85%" },
                  { symbol: "SENSEX", price: marketData.sensex.formattedPrice, change: marketData.sensex.change > 0 ? `+${marketData.sensex.change}%` : `${marketData.sensex.change}%` },
                  { symbol: "RELIANCE", price: "₹2,850", change: "+0.45%" },
                  { symbol: "TCS", price: "₹3,950", change: "+0.75%" },
                  { symbol: "HDFC", price: "₹1,650", change: "+0.35%" }
                ].map((stock, idx) => (
                  <div key={idx} className="flex items-center space-x-4 mx-4">
                    <span className="font-bold text-gray-700">{stock.symbol}</span>
                    <span className="text-gray-600">{stock.price}</span>
                    <span className={`flex items-center ${stock.change.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                      {stock.change.startsWith('+') ? <FaArrowUp className="mr-1" /> : <FaArrowDown className="mr-1" />}
                      {stock.change}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Center Elements */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 hidden lg:flex space-x-8 opacity-30">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-4 border-blue-300 border-t-transparent rounded-full"
        />
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-purple-300 border-b-transparent rounded-full"
        />
      </div>

      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        
        @keyframes marquee {
          0% {
            transform: translateX(100%);
          }
          100% {
            transform: translateX(-100%);
          }
        }
        
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }
        
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }
        
        .animation-delay-1000 {
          animation-delay: 1s;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </section>
  );
};

export default HeroSection;